import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useDestination } from "../../../apis/destination/DestinationProvider";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import Destination from "../../../types/destination";
import { FaChevronDown } from "react-icons/fa";
import AlertOverlay from "../../../components/Alert";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useWarehouse } from "../../../apis/warehouse/WarehouseProvider";

const ViewDestinationById: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { id } = useParams<{ id: string }>();
    const { fetchDestinationById, excluiDestination, putDestination } = useDestination();
    const [destination, setDestination] = useState<Destination | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const selectRef = useRef<(HTMLSelectElement | null)>(null);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const { fetchWarehouse, warehouses } = useWarehouse();
    const [selectedWarehouses, setSelectedWarehouses] = React.useState([] as any[]);

    const navItems: NavBarItem[] = [
        { label: t('destination.labels.home'), route: "/", iconName: "house-door" },
        { label: t('destination.labels.list'), route: "/Destination", iconName: "list" },
        { label: t('destination.labels.new'), route: "/Destination/create", iconName: "plus-circle" },
    ];

    const kinds: string[] = [ "airport", "port" ]

    useEffect(() => {
        if (!id) throw new Error("ID inválido");

        const loadDestination = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const data = await fetchDestinationById(Number(id));

                fetchWarehouse(null);
                
                if (data?.warehouses) {
                    setSelectedWarehouses(data.warehouses.map(warehouse => warehouse.id));
                }
                setDestination(data);
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadDestination();
    }, [id, fetchDestinationById, fetchWarehouse]);

    useEffect(() => {
        const rows = (isEditing)? warehouses : destination?.warehouses;

        if (!rows) {
            return;
        }

        const styleElementId = 'dynamic-row-colors';
        let styleTag = document.getElementById(styleElementId);
        
        if (!styleTag) {
            styleTag = document.createElement('style');
            styleTag.id = styleElementId;
            document.head.appendChild(styleTag);
        }

        const uniqueColors = Array.from(new Set(
            rows.map(r => r.color).filter(c => /^([0-9A-F]{3}){1,2}$/i.test(c ?? ""))
        ));

        const uniqueFontColors = Array.from(new Set(
            rows.map(r => r.fontColor).filter(c => /^([0-9A-F]{3}){1,2}$/i.test(c ?? ""))
        ));
        
        styleTag.innerHTML = uniqueColors.map(color => {
            const className = `row-color-${color}`;
            return `
            .MuiDataGrid-row.${className} {
                background-color: #${color} !important;
            }
            `;
        }).join('\n');

        styleTag.innerHTML += uniqueFontColors.map(color => {
            const className = `row-font-color-${color}`;
            return `
            .MuiDataGrid-row.${className} {
                color: #${color} !important;
            }
            `;
        }).join('\n');
    }, [warehouses, destination?.warehouses, isEditing]);

    const handleEdit = async () => {
        if (!destination) return;

        if (isEditing && destination.id) {
            try {
                const nullRequiredFields: string[] = [];

                if (!destination.description) nullRequiredFields.push("Description");
                if (!destination.kind) nullRequiredFields.push("Kind");
                if (!destination.location?.latitude) nullRequiredFields.push("Lat");
                if (!destination.location?.longitude) nullRequiredFields.push("Lng");
                if (!destination.location?.address?.street) nullRequiredFields.push("Street");
                if (!destination.location?.address?.number) nullRequiredFields.push("Number");
                if (!destination.location?.address?.complement) nullRequiredFields.push("Complement");
                if (!destination.location?.address?.city) nullRequiredFields.push("City");
                if (!destination.location?.address?.state) nullRequiredFields.push("State");
                if (!destination.location?.address?.country) nullRequiredFields.push("Country");
                // if (!destination.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

                if (nullRequiredFields.length > 0) {
                    setInvalidFields(new Set(nullRequiredFields));
                    const warnMessage: string =
                        nullRequiredFields.length === 1
                            ? getUiInterface("fieldRequiredSingle", {
                                field: nullRequiredFields[0],
                            })
                            : getUiInterface("fieldRequiredMultiple", {
                                fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                                    ? ` ${getWordConnector("and")} `
                                    : ""
                                    }${nullRequiredFields.slice(-1)}`,
                            });

                    setAlert({ message: warnMessage, variant: "danger" });
                    return;
                } else {
                    setInvalidFields(new Set());
                }

                const sucesso = await putDestination(destination.id, destination);

                if (sucesso) {
                    const successMessage = getSuccessMessage("editDestination", { id: destination.id, });
                    navigate(`/Destination`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                } else {
                    const errorMessage = getErrorMessage("editDestinationError", { id: destination.id, });
                    setAlert({ message: errorMessage, variant: "danger" });
                }
            } catch (error) {
                setAlert({ message: "Erro ao atualizar os dados.", variant: "danger" });
            }
        } else {
            setIsEditing(true);
        }
    };


    const handleDelete = async () => {
        if (!id) return;

        try {
            const sucesso = await excluiDestination(Number(id));

            if (sucesso) {
                const successMessage = getSuccessMessage("deleteDestination", { id: id });
                navigate(`/destination`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                const errorMessage = getErrorMessage("deleteDestinationError", { id: id, });
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (destination) {
            const { name, value } = e.target;
    
            setDestination((prevDestination) => {
                const keys = name.split(".");
                const updatedDestination = { ...prevDestination };
                let current: any = updatedDestination;
    
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
    
                current[keys[keys.length - 1]] = value;
                
                return updatedDestination;
            });
        }
    };

    const handleSelectionWarehouseChange = (selectionModel: any) => {
        setSelectedWarehouses(selectionModel);
        setDestination((prevValues) => {
            return {
                ...prevValues,
                warehouses: warehouses?.filter(warehouse => selectionModel.includes(warehouse.id))
            }
        })
    };
    
    const warehouseColumns: GridColDef[] = [
        { field: "id",headerName: t('destination.fields.id'),width: 80 },
        { field: "cnpj", headerName: t('client.fields.cnpj'), width: 200 },
        { field: "identification", headerName: t('destination.fields.identification'), width: 200 },
        { field: "name", headerName: t('client.fields.name'), width: 200 },
        { field: "sz", headerName: t('destination.fields.sz'), width: 200 },
    ];
    
    const clientColumns: GridColDef[] = [
        { field: "id",headerName: t('client.fields.id'),width: 80 },
        { field: "cnpj", headerName: t('client.fields.cnpj'), width: 200 },
        { field: "name", headerName: t('client.fields.name'), width: 350 },
    ];

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !destination) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editDestination") : getPageName("viewDestination")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group>
                            <Form.Label>{t('destination.fields.id')}</Form.Label>
                            <Form.Control size="sm" readOnly value={destination.id} style={{ width: "100%", backgroundColor: "#f0f0f0" }} />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="description">
                            <Form.Label>{t('destination.fields.description')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="description"
                                value={destination.description}
                                isInvalid={invalidFields.has("Description")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('destination.fields.kind')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    as="select"
                                    name="kind"
                                    value={destination.kind}
                                    isInvalid={invalidFields.has("Kind")}
                                    readOnly={!isEditing}
                                    onChange={handleChange}
                                    ref={(el: HTMLSelectElement | null) => {
                                        if (el) selectRef.current = el;
                                    }}
                                    style={{ 
                                        backgroundImage: 'none', 
                                        appearance: "none", 
                                        paddingRight: "30px",
                                        cursor: "pointer" 
                                    }}
                                >
                                    <option value="">{t('destination.labels.selectKind')}</option>
                                    {
                                        (kinds || []).map((kind) => (
                                            <option
                                                key={kind}
                                                value={kind}>
                                                {`${kind}`}
                                            </option>
                                        ))
                                    }
                                </Form.Control>
                                <FaChevronDown
                                    style={{
                                        position: "absolute",
                                        right: "20px",
                                        pointerEvents: "auto",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => selectRef?.current?.focus()}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h5>{t('destination.labels.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('destination.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={destination.location?.latitude}
                                isInvalid={invalidFields.has("Lat")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('destination.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={destination.location?.longitude}
                                isInvalid={invalidFields.has("Lng")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('destination.labels.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('destination.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={destination.location?.address?.street}
                                isInvalid={invalidFields.has("Street")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('destination.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={destination.location?.address?.number}
                                isInvalid={invalidFields.has("Number")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('destination.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={destination.location?.address?.complement}
                                isInvalid={invalidFields.has("Complement")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('destination.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={destination.location?.address?.city}
                                isInvalid={invalidFields.has("City")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('destination.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={destination.location?.address?.state}
                                isInvalid={invalidFields.has("State")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('destination.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={destination.location?.address?.country}
                                isInvalid={invalidFields.has("Country")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    {/* <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={destination.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                readOnly={!isEditing}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col> */}
                </Row>

                {
                    !isEditing ?
                    <>
                        <Row>
                            <h6>{t('destination.labels.clients')}</h6>
                            <Col md={12} className="mb-3">
                                <DataGrid
                                    rows={destination.clients}
                                    columns={clientColumns}
                                    checkboxSelection={false}
                                    getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                                    sx={{
                                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                            backgroundColor: "#f5f5f5",
                                            },
                                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                            backgroundColor: "white",
                                            },
                                            "& .MuiDataGrid-row:hover": {
                                            backgroundColor: "#b2d1ff",
                                        },
                                        "& .MuiTablePagination-selectLabel": {
                                            marginTop: "14px",
                                        },
                                        "& .MuiTablePagination-displayedRows": {
                                            marginTop: "14px",
                                        },
                                    }}
                                />
                            </Col>
                        </Row>
                    </>
                    :
                    <>
                    </>
                }
                
                <Row>
                    <h6>{t('destination.labels.warehouses')}</h6>
                    <Col md={12} className="mb-3">
                        <DataGrid
                            rows={(isEditing)? (warehouses || []): destination.warehouses}
                            columns={warehouseColumns}
                            checkboxSelection={isEditing}
                            onRowSelectionModelChange={handleSelectionWarehouseChange}
                            rowSelectionModel={(isEditing)? (selectedWarehouses || []): []}
                            getRowClassName={(params: any) => {
                                const color = params.row.color;
                                const font_color = params.row.fontColor;
            
                                let className = "";
                                if (color && /^([0-9A-F]{6})$/i.test(color)) {
                                    className += `row-color-${color} `;
                                }
                                if (font_color && /^([0-9A-F]{6})$/i.test(font_color)) {
                                    className += `row-font-color-${font_color} `;
                                }
                                
                                if (className && className.length > 0) {
                                    return className;
                                }
                                return params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row";
                            }}
                            sx={{
                                    "& .MuiDataGrid-row:nth-of-type(even)": {
                                    backgroundColor: "#f5f5f5",
                                    },
                                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: "white",
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#b2d1ff",
                                },
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "14px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "14px",
                                },
                            }}
                        />
                    </Col>
                </Row>
                
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewDestinationById;
